import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  SohoBusyIndicatorDirective,
  SohoMessageService,
  SohoModalDialogRef,
  SohoModalDialogService,
} from 'ids-enterprise-ng';
import { LMBannerService } from './lm-banner.service';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMLoginService, LoginDialogStatus, LoginResult } from '@login/lm-login.service';
import {
  LMActionFormDialogResult,
  LMActionFormDialogResultCode,
  LMActionFormDialogService
} from '@modal/lm-action-form-dialog.service';
import { LMSpecService } from '@services/lm-spec.service';
import { EMPTY, forkJoin, interval, Observable, of, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { catchError, debounceTime, first, switchMap, takeUntil } from 'rxjs/operators';
import { LMContextService } from '@services/lm-context.service';
import { LMContextUtilService } from '@utils/lm-context-util.service';
import {
  LMReportPersonalizationDialogResult,
  LMReportPersonalizationDialogResultCode,
  LMReportPersonalizationDialogService
} from '@modal/lm-report-personalization-dialog.service';
import { LMGlobalUtilService } from '@utils/lm-global-util.service';
import { LMGlobalPersonalization } from '@states/models/lm-global-personalization.model';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import * as _ from 'lodash';
import * as urltemplate from 'url-template';
import {
  AsOfDateDialogStatus,
  AsOfDateResult,
  LMBannerAsOfDateDialogService
} from './lm-banner-asofdate-dialog.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMBannerSearchService } from './lm-banner-search.service';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import {
  LMBannerUserContextDialogService,
  LMBannerUserContextDialogStatus,
  LMBannerUserContextResult
} from './lm-banner-usercontext-dialog.service';
import { LMFormRequestService } from '@form/services/lm-form-request.service';
import { AppConstants, LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { Router } from '@angular/router';
import { LMBannerProxyMenuComponent } from './lm-banner-proxy-menu.component';
import {LMApplicationMenuSettingsDialogComponent} from '@app/components';
import {
  LMRoamingUIProfileService,
  RoamingUIProfileResponse,
  RoamingUIProfileResult
} from '@app/services';

interface MyMenuButton {
  icon: string;
  label: string;
  id: string;
  url: string;
}

@Component({
  selector:        'lm-banner-nav',
  templateUrl:     './lm-banner-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls:       [ 'lm-banner-nav.component.scss' ],
  providers: [
    LMRoamingUIProfileService
  ],
})
export class LMBannerNavComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() visibility: string;

  @Input() set asOfDate(date: string) {
    this.allowClearAsOfDate = date !== undefined && date !== '';

    this.m_asOfDate = date;

    // only load menu when initial load has been done
    if (this.bannerMenuSpecs) {
      this.menuSpecs = [];
      this._loadMenu(this.lmUrlUtilService.getBannerSpecUrl());
    }
  }

  get asOfDate() {
    return this.m_asOfDate;
  }

  @Input() applyColorToMenu: boolean;

  @HostBinding('style.visibility') get hostVisibility() {
    return this.visibility;
  };

  @HostListener('click', [ '$event' ]) onClick(event) {
    let closeActionMenu = true;

    const eventPath = (event?.composedPath && event?.composedPath()) || event?.path;
    eventPath?.forEach((element) => {
      if (element?.classList?.contains('lm-banner-nav-action-menu') ||
          element?.classList?.contains('lm-banner-nav-menu-settings-btn')
      ) {
        closeActionMenu = false;
      }
    });

    if(closeActionMenu && this.actionMenuIsOpen) {
      this.closeActionMenu();
    }
  };

  @Output() menuLoaded: EventEmitter<any> = new EventEmitter<any>();
  @Output() menuStateRestored: EventEmitter<any> = new EventEmitter<any>();
  @Output() webAppMenuLoaded: EventEmitter<any> = new EventEmitter<any>();
  @Output() menuSearched: EventEmitter<any> = new EventEmitter<any>();
  @Output() asOfDateChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() asOfDateCleared: EventEmitter<string> = new EventEmitter<string>();
  @Output() onRoleSelected = new EventEmitter<any>();
  @Output() themeChanged: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('UserSettingsDialog', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;
  @ViewChild('AccessDeniedDialog', { read: ViewContainerRef, static: true }) accessDeniedplaceholder: ViewContainerRef;
  @ViewChild('LoginDialog', { read: ViewContainerRef, static: true }) loginPlaceholder: ViewContainerRef;
  @ViewChild('ChangePasswordDialog', { read: ViewContainerRef, static: true }) changePasswordPlaceholder: ViewContainerRef;
  @ViewChild('DrillbackActionFormDialog', { read: ViewContainerRef, static: true }) drillbackPlaceholder: ViewContainerRef;
  @ViewChild('ReportPersonalizationDialog', { read: ViewContainerRef, static: true }) reportPlaceholder: ViewContainerRef;
  @ViewChild('SetAsOfDateDialog', { read: ViewContainerRef, static: true }) setAsOfDatePlaceholder: ViewContainerRef;
  @ViewChild('UserContextDialog', { read: ViewContainerRef, static: true }) userContextPlaceholder: ViewContainerRef;
  @ViewChild('webAppMenuHeader') private _webAppMenu: ElementRef;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild('ActionMenu') actionMenu: ElementRef;

  actionMenuIsOpen = false;
  mingleMode: boolean;
  bannerMenuSpecs: Array<LMWebAppSpec>;
  webAppMenuSpecs: Array<any>;  // todo: type me!
  aboutLink: string;
  appHelpLink: string;
  myNotificationsUrl: string;
  myInBasketUrl: string;
  myJobsUrl: string;
  myPrintFilesUrl: string;
  myActionsUrl: string;
  myReportsUrl: string;
  myPersonalizationsUrl: string;
  mySavedSearchesUrl: string;
  actorName: string;
  anonymousUser: boolean;
  proxyMode: boolean;
  notificationEnabled: boolean;
  inbasketEnabled: boolean;
  personalizationEnabled: boolean;
  enableSavedSearch: boolean;
  proxyEnabled: boolean;
  selectedMenuSpec: Array<any>; // todo: type me!
  menuSpecs: Array<any>; // todo: type me!
  searchMenuSpecs: Array<any>;
  webAppTitle: string;
  webAppName: string;
  selectedWebAppName: string;
  webAppSuiteLink: string;
  appSwitcher: LMBannerAppMenuMode;
  webAppMenuNavigationUrl: string;

  signOutLink: string;
  signInLink: string;
  registerLink: string;
  changePasswordLink: string;
  proxyManagementLink: string;
  createReportUrl: string;
  actorGroupSelectUrl: string;
  roleSelectUrl: string;
  roleGroupSelectUrl: string;
  reportFieldSearchUrl: string;
  enableUserContextMenu: boolean;

  hasRegisterLink: boolean;
  hasChangePasswordLink: boolean;

  allowSetAsOfDate: boolean;
  allowClearAsOfDate: boolean;

  searchMode: boolean;
  updateRequired: boolean;
  roleSwitcher: boolean;

  private notificationRefreshInterval: number;
  private unreadNotificationCount: number;
  private _displayRedDot = false;
  private m_asOfDate: string;
  public noCasualApps: number;

  public get displayRedDot(): boolean {
    return this._displayRedDot;
  }

  public set displayRedDot(display: boolean) {
    this._displayRedDot = display;
  }

  public get isPasswordReset(): boolean {
    return this.lmContextService.getContext().completePasswordReset;
  }

  private stopInterval$ = new Subject<boolean>();
  private pause$ = new Subject<boolean>();
  public searchFieldText$ = new Subject<string>();
  private destroyer$ = new Subject<void>();

  public clientLogin: boolean;
  public actorUrl: string;
  private dialog: SohoModalDialogRef<any>;
  private subscriptionArray: Array<ISubscription> = []; // todo: type me!
  public proxyUserUrls: any;
  public proxyWebApps: Array<string> = [];  // todo: type me!
  public proxyResetUrls: any;
  public menuSearchField: LMToolbarSearchField;
  public model = {
    searchFieldValue: ''
  };
  public menuSearchResultsByWebApp: LMBannerSearchResults;
  public reportSearchResult: Array<LMBannerSearchResult>;
  public dataSearchResult: Array<LMBannerSearchResult>;

  public myMenuButtons: MyMenuButton[] = [];
  public myOverflowMenuButtons: MyMenuButton[] = [];
  public showOverFlowMenuButton: boolean;

  public configFeaturesEnabled: boolean;
  public showProxy: boolean;
  public showSearch: boolean;
  public allowAnonymousAccess: boolean;

  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmAsOfDateService: LMBannerAsOfDateDialogService,
    private lmContextService: LMContextService,
    private lmBannerService: LMBannerService,
    private lmFormRequestService: LMFormRequestService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmSpecService: LMSpecService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmLoginService: LMLoginService,
    private lmContextUtilService: LMContextUtilService,
    private lmReportPersonalizationDialogService: LMReportPersonalizationDialogService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmUniqueIdService: LMUniqueIdService,
    private sohoMessageService: SohoMessageService,
    private lmBannerSearchService: LMBannerSearchService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmUserContextService: LMBannerUserContextDialogService,
    @Inject(forwardRef(() => LMActionFormDialogService)) private lmActionFormDialogService, // LMActionFormDialogService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private lmRoamingUIProfileService: LMRoamingUIProfileService
  ) {
    this.searchFieldText$.pipe(
      takeUntil(this.destroyer$),
      debounceTime(1000))
      .subscribe(value => {
        const length = value ? value.length : 0;
        if (length > 2) {
          this.bannerSearch();
        }
      });

  }

  ngOnInit() {
    this.lmLoggerService.trace('@BannerNavComponent -> ngOnInit');

    this.searchMode = false;
    this.updateRequired = false;
    this.menuSpecs = [];
    this.roleSwitcher = false;

    this.webAppSwitcherButtonId = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1 ? 'trigger-btn' : 'webapp-menu';

    this.getBannerPersonalization();

    this.allowAnonymousAccess = this.lmContextService.allowAnonymousAccess();
    this.configFeaturesEnabled = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_ConfigurableFeatures);
    this.showSearch = this.evaluateShowSearch();
  }

  ngAfterViewInit() {
    this.mingleMode = this.lmContextService.inMingle();

    this._loadMenu(this.lmUrlUtilService.getBannerSpecUrl());
  }

  ngOnDestroy() {
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
    this.stopInterval$.next(true);
    this.stopInterval$.complete();
    this.pause$.complete();
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  /**
   * Set href url when a right click is executed
   * @param event
   * @param menuItem
   */
  public onRoleRightClick(event: MouseEvent, menuItem: LMMenuItemSpec) {
    if (menuItem && menuItem.webAppUrl) {
      (event.currentTarget as HTMLElement).setAttribute('href', menuItem.webAppUrl);
    }
  }

  public selectRole($event?: any) {
    // Reset href to hash for regular click events.
    if ($event) {
      ($event.currentTarget as HTMLElement).setAttribute('href', '#');
    }

    this.menuSpecs = [];
    this.roleSwitcher = true;
    this.changeDetectorRef.detectChanges();
    this.onRoleSelected.emit();

    this.showBusyIndicator();

    const button: any = $event.currentTarget;
    let url = button.getAttribute('navigation-menu-url');

    this.webAppTitle = button.getAttribute('title');
    this.webAppName = button.getAttribute('web-app-name');

    // For webapp without a navigation menu
    if (!url) {
      this.selectedMenuSpec = [];
      this.webAppMenuSpecs = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
      const currWebAppSpec = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);

      this.selectedMenuSpec.push(
        {
          resourceId:   currWebAppSpec[ 0 ].webAppName,
          name:         'Home',
          link:         currWebAppSpec[ 0 ].webAppUrl,
          id:           'Home',
          label:        Soho.Locale.translate('Home'),
          resourceType: 'page'
        });

      this.buildMenuSpecs(this.menuSpecs, this.webAppName, this.webAppTitle, this.selectedMenuSpec);
      this.markForRefresh();
      this.updateRequired = true;

      this.lmContextService.updateWebApp(currWebAppSpec[ 0 ]);
      this.lmGlobalUtilService.navigateByUrl(currWebAppSpec[ 0 ].webAppUrl);

      this.hideBusyIndicator();
    } else {

      const loadAllLevels = this.lmBannerService.loadAllLevels();

      if (loadAllLevels) {
        url = `${url}&allLevels=true`;
      }

      this.subscriptionArray.push(
        this.lmSpecService.getBannerSpec(url).subscribe(
          response => {
            if (loadAllLevels) {
              this.saveAllLevelsResponse(response);
            }

            this.selectedMenuSpec = response.menuSpecs;

            this.buildMenuSpecs(this.menuSpecs, response.webApp, response.webAppTitle, this.selectedMenuSpec);

            this.signOutLink = response.signOutLink;

            this.resetMenuPersonalization();

            this.lmBannerService.setBannerMenuSpec(this.selectedMenuSpec);

            this.lmBannerService.updateBannerMenuSpec(response.menuSpecs, url);

            this.markForRefresh();
            this.updateRequired = true;

            this.webAppMenuSpecs = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);

            const currWebAppSpec = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);
            this.lmContextService.updateWebApp(currWebAppSpec[ 0 ]);

            this.lmGlobalUtilService.navigateByUrl(currWebAppSpec[ 0 ].webAppUrl);

            this.hideBusyIndicator();
          },
          error => {
            this._handleError(error);
          })
      );
    }
  }

  public get webAppMenuHeader(): ElementRef {
    const webAppMenuName = this.selectedWebAppName ? this.selectedWebAppName : this.webAppName;
    // @ts-ignore

    if (this._webAppMenu && this._webAppMenu.nativeElement.getAttribute('id') === webAppMenuName) {
      return this._webAppMenu;
    }
    return null;
  }

  public onMenuLoaded(event) {
    this.menuLoaded.emit(event);
  }

  public onWebAppMenuLoaded() {
    this.webAppMenuLoaded.emit(this.roleSwitcher);
  }

  public onMenuSearched(event: LMBannerSearchEvent) {
    this.menuSearched.emit(event);
  }

  public openUserSettingsDialog() {
    const dialogId = 'UserSettings';
    const buttons = [
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: (e, modal) => {
          modal.isCancelled = true;
          modal.close(true);
        }
      },
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.SAVE ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.SAVE),
        click: (e, modal) => {
          modal.close(true);
        },
        isDefault: true
      }
    ];

    this.dialog = this.sohoModalDialogService
      .modal(LMApplicationMenuSettingsDialogComponent, this.placeholder, { fullsize: 'responsive' })
      .buttons(buttons)
      .title(Soho.Locale.translate(dialogId))
      .open();
  }

  public openSetAsOfDateDialog(): void {
    const date = this.lmContextService.getContext().asOfDate ? this.lmFormatUtilService.yyyymmddToDisplay(this.asOfDate) : null;
    this.lmAsOfDateService.promptAsOfDateDialog(this.setAsOfDatePlaceholder, date)
      .pipe(first())
      .subscribe((result: AsOfDateResult) => {
        if (result.status !== AsOfDateDialogStatus.RESPONSE_OK) {
          return;
        }
        this.lmLoggerService.log(result.date);
        const dbDate = this.lmFormatUtilService.yyyymmddToData(result.date);
        this.asOfDateChanged.emit(dbDate);
      });
  }

  public clearAsOfDate() {
    this.asOfDateCleared.emit();
  }

  public openSetUserContextDialog() {
    const userContextUrl = this.lmUrlUtilService.getUserContextUrl();

    this.lmSpecService.getBannerSpec(userContextUrl).subscribe(
      (response: any) => {
        const userContextDialog = this.lmUserContextService.promptUserContextDialog(this.userContextPlaceholder, response.userContextFields)
          .subscribe(
            (result: LMBannerUserContextResult) => {
              if (result.status === LMBannerUserContextDialogStatus.RESPONSE_OK) {
                if (result.userContextProperties && result.saveChanges) {

                  const jsonContextPropertiesArray = [];
                  Object.keys(result.userContextProperties).forEach(key => {
                    const userContextModel: LMBannerUserContextModelField = result.userContextProperties[ key ];
                    if (userContextModel.dirty) {
                      const jsonContextProperty = {
                        id:    key,
                        value: userContextModel.value
                      };
                      jsonContextPropertiesArray.push(jsonContextProperty);
                    }
                  });

                  this.lmBannerService.updateActorContext(userContextUrl, jsonContextPropertiesArray).subscribe((ucResponse: any) => {
                    if (ucResponse.status === 'COMPLETED') {
                      window.location.href = this.lmContextService.getCurrentWebAppSpec().webAppUrl;
                    }
                  });
                }
              }
            }
          );
        this.markForRefresh();
        // this.updateRequired = true;
        this.subscriptionArray.push(userContextDialog);
      },
      error => {
        this._handleError(error);
      });
    return false;
  }

  public noActorUrl() { // tslint:disable-line (template)
    return (this.actorUrl === '#' || this.actorUrl == null);
  }

  public openLink(link: string) {
    if (link === this.myNotificationsUrl) {
      this.displayRedDot = false;
      // Convert timestamp from simplified ISO format (YYYY-MM-DDTHH:mm:ss.sssZ) to Landmark format (yyyyMMddHHmmssSS)
      const curTimeStamp = new Date().toISOString().slice(-24).replace(/\D/g, '').slice(0, 16);
      this.lmRoamingUIProfileService.setLastViewedNotification(curTimeStamp)
        .subscribe(
          (results: RoamingUIProfileResponse) => {
            if (results.status === RoamingUIProfileResult.COMPLETED) {
              this.lmLoggerService.log('RoamingUIProfile Updated');
            }
          });
    }
    const compiledTemplate = urltemplate.parse(link);
    link = compiledTemplate.expand({
      urlPdlOrDa: this.lmContextService.getUrlPdlOrDa(),
      webApp:     this.lmContextService.getCurrentWebAppSpec().webAppName
    });
    this.lmContextService.setClearNextPageState(true);
    this.lmGlobalUtilService.navigateByUrl(link);
  }

  public openHelpLink() {
    this.lmUrlUtilService.openExternalUrl(this.appHelpLink);
  }

  public openSecurityLink(link: string) {
    this.resetLastOpenedMenu();
    window.location.href = link;
  }

  private resetLastOpenedMenu() {
    const bannerPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();

    if (!_.isEmpty(bannerPersonalization) && bannerPersonalization.lastOpenedMenu) {
      bannerPersonalization.lastOpenedMenu = '';
    }
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(bannerPersonalization);
  }

  public _loadMenu(url: string, syncWebApp?: boolean): void {
    this.lmLoggerService.trace('@BannerNavComponent -> loadBannerSpec');
    this.showBusyIndicator();

    if (syncWebApp) {
      this.webAppMenuSpecs = [];
      this.markForRefresh();
      this.updateRequired = true;
    }

    this.subscriptionArray.push(this.lmSpecService.getBannerSpec(url).subscribe(
      (bannerSpec: LMBannerSpec) => {
        this.bannerMenuSpecs = bannerSpec.webApps;
        this.appSwitcher = 'casual'; // bannerSpec.appSwitcher || 'accordion';
        this.noCasualApps = bannerSpec.noCasualApps;
        this.aboutLink = bannerSpec.aboutLink;
        this.appHelpLink = bannerSpec.appHelpLink;
        this.myActionsUrl = bannerSpec.myActionsUrl;
        this.myInBasketUrl = bannerSpec.myInBasketUrl;
        this.myJobsUrl = bannerSpec.myJobsUrl;
        this.myNotificationsUrl = bannerSpec.myNotificationsUrl;
        this.myPrintFilesUrl = bannerSpec.myPrintFilesUrl;
        this.myReportsUrl = bannerSpec.myReportsUrl;
        this.myPersonalizationsUrl = bannerSpec.myPersonalizationsUrl;
        this.mySavedSearchesUrl = bannerSpec.mySavedSearchesUrl;
        this.createReportUrl = this.evaluateCreateReportUrl(bannerSpec);
        this.actorGroupSelectUrl = bannerSpec.actorGroupSelectUrl;
        this.roleSelectUrl = bannerSpec.roleSelectUrl;
        this.roleGroupSelectUrl = bannerSpec.roleGroupSelectUrl;
        this.reportFieldSearchUrl = bannerSpec.reportFieldSearchUrl;
        this.signOutLink = bannerSpec.signOutLink;
        this.registerLink = bannerSpec.registerLink;
        this.signInLink = bannerSpec.signInLink;
        this.changePasswordLink = bannerSpec.changePasswordLink;
        this.actorName = bannerSpec.actorInfo.displayName[ 0 ];
        this.actorUrl = bannerSpec.actorInfo.actorUrl[ 0 ];

        // Proxy
        this.proxyUserUrls = bannerSpec.proxyUserUrls;
        if (this.proxyUserUrls) {
          this.proxyWebApps = Object.keys(this.proxyUserUrls);
        }
        this.proxyResetUrls = bannerSpec.proxyResetUrls;
        this.proxyMode = bannerSpec.proxyMode || false;
        this.proxyEnabled = bannerSpec.proxyEnabled || false;
        this.proxyManagementLink = bannerSpec.proxyManagementLink;
        this.showProxy = this.evaluateProxy(bannerSpec);

        this.anonymousUser = bannerSpec.anonymousUser || false;
        this.clientLogin = bannerSpec.clientLogin || false;
        this.webAppMenuNavigationUrl = bannerSpec.webAppMenuNavigationUrl;
        if (this.lmGlobalPersonalizationService.getGlobalPersonalization().preferredWebApp !== undefined) {
          const preferredAppName = this.lmGlobalPersonalizationService.getGlobalPersonalization().preferredWebApp;
          const preferredAppSpec = this.bannerMenuSpecs.find((preferredApp: LMWebAppSpec) => preferredApp.webAppName === preferredAppName);
          if ((this.lmContextService.getContext().webApp === preferredAppName) && preferredAppSpec !== undefined) {
            this.webAppMenuNavigationUrl = preferredAppSpec.navigationMenuUrl;
          }
        }
        this.webAppTitle = this.lmContextService.getContext().webAppTitle;
        this.webAppName = this.lmContextService.getContext().webApp;
        this.selectedWebAppName = this.webAppName;
        this.webAppSuiteLink = bannerSpec.webAppSuiteLink;

        this.hasRegisterLink = (this.registerLink === '' || this.registerLink == null) ? false : true;
        this.hasChangePasswordLink = (this.changePasswordLink === '' || this.changePasswordLink == null) ? false : true;

        // this.allowSetAsOfDate = bannerSpec.allowSetAsOfDate;
        this.allowSetAsOfDate = this.evaluateAsOfDate(bannerSpec);
        this.allowClearAsOfDate = this.asOfDate !== undefined && this.asOfDate !== '';

        this.enableUserContextMenu = bannerSpec.enableUserContextMenu;

        this.inbasketEnabled = bannerSpec.inbasketEnabled || true;
        this.notificationEnabled = bannerSpec.notificationEnabled || true;
        this.personalizationEnabled = this.evaluatePersonalization(bannerSpec);
        this.enableSavedSearch = this.lmContextService.showUserListView();

        if (this.isPasswordReset) {
          this.hideBusyIndicator();
          this.markForRefresh();
          this.updateRequired = true;
          this.lmLoggerService.trace('@BannerNavComponent -> loadBannerSpec -> complete');
          return;
        }

        this.updateMyMenuButtons();

        if (this.notificationEnabled) {
          this.notificationRefreshInterval = (bannerSpec.refreshIntervalMinutes) ? parseInt(bannerSpec.refreshIntervalMinutes, 10) : 5;
          this.unreadNotificationCount = (bannerSpec.unreadCount) ? parseInt(bannerSpec.unreadCount, 10) : 0;
          this.displayRedDot = this.unreadNotificationCount > 0;

          // Poll to check unread notification count
          const interval$ = interval(this.notificationRefreshInterval * 60 * 1000).pipe(
            takeUntil(this.stopInterval$)
          );
          // Pause during async call to get the notification count.
          const pausable$ = this.pause$.pipe(
            switchMap(paused => paused ? EMPTY : interval$));

          this.subscriptionArray.push(pausable$.subscribe(() => {
            this.getUnreadNotificationCount();
          }));

          this.pause$.next(false);
        }

        if (!this.webAppMenuNavigationUrl) {
          if (this.useAppSwitcher()) {
            this.selectedMenuSpec = [];
            this.webAppMenuSpecs = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
            if (this.webAppMenuSpecs.length === 0 || this.webAppMenuSpecs.length > this.noCasualApps - 1) {
              this.roleSwitcher = true;
            } else {
              this.lmBannerService.clearExpandedMenus();
            }
            const currWebAppSpec = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);

            this.selectedMenuSpec.push(
              {
                resourceId:   this.webAppName,
                name:         'Home',
                link:         this.webAppName === 'web' ? this.webAppSuiteLink : currWebAppSpec[ 0 ].webAppUrl,
                id:           'Home',
                label:        Soho.Locale.translate('Home'),
                resourceType: 'page'
              });

            this.buildMenuSpecs(this.menuSpecs, this.webAppName, this.webAppTitle, this.selectedMenuSpec);

            this.markForRefresh();
            this.updateRequired = true;
          } else {
            this.lmBannerService.updateBannerMenuSpec(bannerSpec.webApps);
          }
        } else {
          if (this.useAppSwitcher()) {
            this.loadWebAppMenuSpec();
          } else {
            this.lmBannerService.updateBannerMenuSpec(bannerSpec.webApps);
          }
        }

        this.hideBusyIndicator();
        this.lmLoggerService.trace('@BannerNavComponent -> loadBannerSpec -> complete');
      },
      error => {
        // acces denied error is handled in app.component.ts
        if (error.status !== 403) {
          this._handleError(error);
        }
      }));
  }

  private saveAllLevelsResponse(response) {
    const self = this;
    const expandedMenus = this.lmBannerService.getExpandedMenus();

    if (expandedMenus && expandedMenus.length > 0) {
      response.menuSpecs.map(function callback(menuItemSpec: LMMenuItemSpec) {
        if (expandedMenus.includes(menuItemSpec.menuIndex)) {
          menuItemSpec.expanded = true;
        }

        if (menuItemSpec.menuIndex === self.lmBannerService.getLastMenuItemClicked()) {
          menuItemSpec.selected = true;
        }

        if (menuItemSpec.menu && menuItemSpec.menu.length > 0) {
          menuItemSpec.menu.map(callback);
        }
      });
    }

    this.lmBannerService.saveAllLevelsResponse(response);
  }

  private loadWebAppMenuSpec() {
    const self = this;
    const loadAllLevels = this.lmBannerService.loadAllLevels();
    let webAppMenuNavigationUrl = this.webAppMenuNavigationUrl;

    if (loadAllLevels) {
      webAppMenuNavigationUrl = `${this.webAppMenuNavigationUrl}&allLevels=true`;
    }

    this.subscriptionArray.push(this.lmSpecService.getBannerSpec(webAppMenuNavigationUrl)
      .subscribe(async response => {
        if (loadAllLevels) {
          this.saveAllLevelsResponse(response);
        } else {
            /**
             *  Check state to see if there were previously expanded menus
             *  Load all levels from previously expanded menus
             *  Only do this if loadAllLevels is false
             */
          if (response.menuSpecs
              && (this.lmBannerService.getExpandedMenus()
                && this.lmBannerService.getExpandedMenus().length > 0)
          ) {
            await Promise.all(response.menuSpecs.map(async function callback(menuItemSpec: LMMenuItemSpec) {
              if (self.lmBannerService.getExpandedMenus().includes(menuItemSpec.menuIndex)) {
                menuItemSpec.menu = await self.getSubMenu(menuItemSpec.link);
                menuItemSpec.expanded = true;

                if (menuItemSpec.menuIndex === self.lmBannerService.getLastMenuItemClicked()) {
                  menuItemSpec.selected = true;
                }

                if (menuItemSpec.menu && menuItemSpec.menu.length > 0) {
                  await Promise.all(menuItemSpec.menu.map(callback));
                }
              }
            }));
          }
        }

        this.selectedMenuSpec = response.menuSpecs;
        this.buildMenuSpecs(this.menuSpecs, response.webApp, response.webAppTitle, this.selectedMenuSpec);
        this.lmBannerService.updateBannerMenuSpec(response.menuSpecs, this.webAppMenuNavigationUrl);
        this.webAppMenuSpecs = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
        if (this.webAppMenuSpecs.length === 0 || this.webAppMenuSpecs.length > this.noCasualApps - 1) {
          this.roleSwitcher = true;
        } else {
          this.lmBannerService.clearExpandedMenus();
        }

        this.markForRefresh();
        this.updateRequired = true;

        this.lmContextUtilService.setDrillbackDialogElement(this.drillbackPlaceholder, this.lmActionFormDialogService);
        const dialogId = 'BannerResponse';

        if (response.buildBaseMenuMessage) {
          this.sohoMessageService
            .message()
            .message(Soho.Locale.translate(response.buildBaseMenuMessage))
            .title('')
            .buttons([
              {
                id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CLOSE ]),
                text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
                click: (e, modal) => {
                  modal.close(true);
                }
              },
            ])
            .open();
        }
      },
      error => {
        this._handleError(error);
      }));
  }

  private async getSubMenu(url: string): Promise<Array<LMMenuItemSpec>> {
    return new Promise((resolve, reject) => {
      this.lmSpecService.getBannerSpec(url).subscribe((response) => {
        resolve(response.menuSpecs);
      });
    });
  }

  /**
   * Data and reporting options are disabled by default.
   * And should display if included in the configured features list.
   * @param buttonName
   * @private
   */
  public showDataAndReportingOptions(buttonName: LMConfiguredFeatures): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return this.lmContextService.getContext().configuredFeatures.includes(buttonName);
  }

  /**
   * Show if not in configured features list.
   * Application menu options are enabled by default.
   * If application menu option is in config features list then it was disabled and shouldn't be displayed
   * @param buttonName
   * @private
   */
  public evaluateApplicationMenuOption(buttonName: LMConfiguredFeatures): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return !this.lmContextService.getContext().configuredFeatures.includes(buttonName);
  }

  /**
   * Determines if proxy should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display proxy
   * @private
   */
  private evaluateProxy(applicationMenuSpec: LMApplicationMenuSpec): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return applicationMenuSpec.proxyEnabled || applicationMenuSpec.proxyMode || applicationMenuSpec.proxyManagementLink !== undefined;
  }

  /**
   * Determines if "as of date" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "as of date"
   * @private
   */
  private evaluateAsOfDate(applicationMenuSpec: LMApplicationMenuSpec) {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return applicationMenuSpec.allowSetAsOfDate;
  }

  /**
   * Determines if "Create Report" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "Create Report"
   * @private
   */
  private evaluateCreateReportUrl(applicationMenuSpec: LMApplicationMenuSpec): string | undefined {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return undefined;
      }
    }

    return applicationMenuSpec.createReportUrl;
  }

  /**
   * Determines if "personalization" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "personalization"
   * @private
   */
  private evaluatePersonalization(applicationMenuSpec: LMApplicationMenuSpec): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return undefined;
      }
    }

    return applicationMenuSpec.personalizationEnabled;
  }

  /**
   * Determines if "search" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "search"
   * @private
   */
  private evaluateShowSearch() {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return undefined;
      }
    }

    return !this.lmContextService.getContext().configuredFeatures.includes('HideMenuSearch');
  }

  private updateMyMenuButtons() {

    // <div class="accordion-header"><a href="#" (click)="runCreateReport()"><span>{{ 'CreateReport' | translate }}</span></a></div>

    this.myMenuButtons = new Array();
    this.myOverflowMenuButtons = new Array();

    if (this.evaluateApplicationMenuOption('HideMenuPrintFiles')) {
      this.myMenuButtons.push({ icon: 'print', label: Soho.Locale.translate('MyPrintFiles'), id: 'MyPrintFiles_button', url: this.myPrintFilesUrl });
    }

    if (this.notificationEnabled && this.evaluateApplicationMenuOption('HideMenuNotifications')) {
      this.myMenuButtons.push({ icon: 'notification', label: Soho.Locale.translate('MyNotifications'), id: 'MyNotifications_button', url: this.myNotificationsUrl });
    }

    if (this.inbasketEnabled && this.evaluateApplicationMenuOption('HideMenuInbasket')) {
      this.myMenuButtons.push({ icon: 'download', label: Soho.Locale.translate('MyInbasket'), id: 'MyInbasket_button', url: this.myInBasketUrl });
    }

    if (this.evaluateApplicationMenuOption('HideMenuJobs')) {
      this.myMenuButtons.push({ icon: 'cascade-objects', label: Soho.Locale.translate('MyJobs'), id: 'MyJobs_button', url: this.myJobsUrl });
    }

    if (this.evaluateApplicationMenuOption('HideMenuReports')) {
      this.myMenuButtons.push({ icon: 'report', label: Soho.Locale.translate('MyReports'), id: 'MyReports_button', url: this.myReportsUrl });
    }

    if (this.evaluateApplicationMenuOption('HideMenuActions')) {
      this.myMenuButtons.push({ icon: 'checkbox', label: Soho.Locale.translate('MyActions'), id: 'MyActions_button', url: this.myActionsUrl });
    }

    if (this.personalizationEnabled) {
      if (this.myMenuButtons.length === 6 && !this.enableSavedSearch) {
        this.myMenuButtons.push({ icon: 'quick-edit', label: Soho.Locale.translate('MyPersonalizations'), id: 'MyPersonalization_button', url: this.myPersonalizationsUrl });
      } else if (this.myMenuButtons.length === 6 && this.enableSavedSearch) {
        this.showOverFlowMenuButton = true;
        this.myOverflowMenuButtons.push({ icon: 'quick-edit', label: Soho.Locale.translate('MyPersonalizations'), id: 'MyPersonalization_button', url: this.myPersonalizationsUrl });
      } else {
        this.myMenuButtons.push({ icon: 'quick-edit', label: Soho.Locale.translate('MyPersonalizations'), id: 'MyPersonalization_button', url: this.myPersonalizationsUrl });
      }
    }

    if (this.enableSavedSearch) {
      if (this.myMenuButtons.length === 6 && !this.personalizationEnabled) {
        this.myMenuButtons.push({ icon: 'print-preview', label: Soho.Locale.translate('MySavedSearches'), id: 'MySavedSearches_button', url: this.mySavedSearchesUrl });
      } else if (this.myOverflowMenuButtons.length === 1 && this.personalizationEnabled) {
        this.showOverFlowMenuButton = true;
        this.myOverflowMenuButtons.push({ icon: 'print-preview', label: Soho.Locale.translate('MySavedSearches'), id: 'MySavedSearches_button', url: this.mySavedSearchesUrl });
      } else {
        this.myMenuButtons.push({ icon: 'print-preview', label: Soho.Locale.translate('MySavedSearches'), id: 'MySavedSearches_button', url: this.mySavedSearchesUrl });
      }
    }
  }

  public toggleActionMenu(e: Event) {
    e?.stopImmediatePropagation();
    this.actionMenu.nativeElement.style.display = this.actionMenuIsOpen ? 'none' : 'block';
    this.actionMenuIsOpen = !this.actionMenuIsOpen;
  }

  public closeActionMenu() {
    this.actionMenu.nativeElement.style.display = 'none';
    this.actionMenuIsOpen = false;
  }

  private useAppSwitcher(): boolean {
    return (this.appSwitcher === 'casual' || this.appSwitcher === 'popup' || this.appSwitcher === 'accordion' || this.appSwitcher === 'suite');
  }

  private markForRefresh() {
    this.changeDetectorRef.markForCheck();
  }

  private _handleError(error: any): void {
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  private getUnreadNotificationCount() {
    let unreadCountUrl = this.lmUrlUtilService.getBannerSpecUrl();
    unreadCountUrl += (unreadCountUrl.indexOf('?') > -1 ? '&' : '?') + 'requestType=unreadCount';
    this.pause$.next(true);
    this.lmSpecService.getBannerSpec(unreadCountUrl).subscribe(
      (response: any) => {
        this.pause$.next(false);
        this.unreadNotificationCount = (response.unreadCount) ? parseInt(response.unreadCount, 10) : 0;
        this.displayRedDot = this.unreadNotificationCount > 0;
        this.markForRefresh();
      },
      error => {
        // In 3.5, the error is logged in the console for unread count and not prompted in dialog.
        // So timeout or when server down will not open up a dialog for a background poll request.
        // this._handleError(error);
        this.lmLoggerService.log([ this, error.status, error.message, 'Get unread notification count failed.' ]);
      });
  }

  public bannerSearch() {
    this.showBusyIndicator();
    const menuSearchUrl = this.lmBannerSearchService.getMenuSearchUrl(this.model.searchFieldValue);
    const observables: Observable<any>[] = [];

    if (this.searchMenuSpecs === undefined) {
      this.searchMenuSpecs = [];
    }

    this.subscriptionArray.push(this.lmSpecService.getRedirectUrl(menuSearchUrl).subscribe(response => {
      const dataMenus = response.dataModules;
      const reports = response.reports;
      const totalWebAppMenu = this.bannerMenuSpecs.length + 1;

      if (totalWebAppMenu > this.searchMenuSpecs.length) {
        this.bannerMenuSpecs.forEach((webApp) => {
          const menu = this.searchMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === webApp.webAppName);
          if (webApp.navigationMenuUrl && menu.length === 0) {
            const url = this.lmUrlUtilService.appendToQueryString(webApp.navigationMenuUrl, 'allLevels=true');
            observables.push(this.lmSpecService.getBannerSpec(url).pipe(catchError(error => of(this.handleForkJoinError(error)))));
          }
        });
        if (observables.length > 1) {
          this.subscriptionArray.push(forkJoin(observables).subscribe(results => {
            if (results.length > 0) {
              results.forEach((result) => {
                if (result && result.webApp) {
                  this.buildMenuSpecs(this.searchMenuSpecs, result.webApp, result.webAppTitle, result.menuSpecs);
                }
              });
            }
            this.searchMenu(dataMenus, reports);
          }));
        } else {
          this.searchMenu(dataMenus, reports);
        }
      } else {
        this.searchMenu(dataMenus, reports);
      }
    },
    error => {
      this.hideBusyIndicator();
      this._handleError(error);
    }));
  }

  private handleForkJoinError(error) {
    if (error.status === 504) {
      console.error(`Searching ${error.error.webApp} has timed out`);
    }
  }

  private searchMenu(dataMenus: any, reports: any) {
    this.menuSearchResultsByWebApp = this.lmBannerSearchService.matchMultiMenus(this.menuSpecs, this.model.searchFieldValue);
    this.dataSearchResult = this.lmBannerSearchService.matchData(dataMenus);
    this.reportSearchResult = this.lmBannerSearchService.matchReports(reports);

    this.searchMode = true;

    const event: LMBannerSearchEvent = {
      searchString:              this.model.searchFieldValue,
      menuSpecsByWebApp:         this.menuSpecs,
      menuSearchResultsByWebApp: this.menuSearchResultsByWebApp,
      dataSearchResults:         this.dataSearchResult,
      reportSearchResults:       this.reportSearchResult,
      toggleMenu:                Object.keys(this.menuSearchResultsByWebApp).length > 0 ? 'Applications' : this.reportSearchResult.length > 0 ? 'Reports' : 'Data'
    };
    this.model.searchFieldValue = '';
    this.menuSearched.emit(event);
    this.hideBusyIndicator();
  }

  autoSearchMenu(searchValue: string) {
    this.searchFieldText$.next(searchValue);
  }

  // Save this code for menu search implementation
  fireMenuSearchEvent(event: Event) {
    if (!this.model.searchFieldValue) {
      // if value is blank then fire clearSearchEvent event
      this.fireClearSearchEvent(event);
    } else {
      // if searchfieldvalue length is 3 or more the auto search with perform this.bannerSearch();
      if (this.model.searchFieldValue.length <= 2) {
        this.bannerSearch();
      }
    }
  }

  fireClearSearchEvent(event) {
    this.searchMode = false;

    const toolbarSearchEvent: LMToolbarSearchEvent = {
      event,
      searchField: this.menuSearchField
    };
  }

  public openProxyPage() {
    const dialogId = 'Proxy';
    let title: string;

    if (this.proxyEnabled) {
      title = Soho.Locale.translate('SwitchToProxy');
    } else if (this.proxyMode) {
      title = Soho.Locale.translate('SwitchBackFromProxy');
    } else {
      title = Soho.Locale.translate('ProxyManagement')
    }

    let proxyMenuComponent: LMBannerProxyMenuComponent;
    this.dialog = this.sohoModalDialogService
      .modal(LMBannerProxyMenuComponent, this.placeholder, {fullsize: 'responsive'})
      .apply((proxyMenu: LMBannerProxyMenuComponent) => {
        proxyMenuComponent = proxyMenu;
        if (this.proxyMode) {
          proxyMenuComponent.proxyMode = this.proxyMode;
          proxyMenuComponent.proxyResetUrls = this.proxyResetUrls;
        }
        if (this.proxyEnabled) {
          proxyMenuComponent.proxyEnabled = this.proxyEnabled;
          proxyMenuComponent.proxyUserUrls = this.proxyUserUrls;
        }
        if (this.proxyManagementLink) {
          proxyMenuComponent.proxyManagementLink = this.proxyManagementLink;
        }
      })
      .buttons([
        {
          id: this.lmUniqueIdService.uniqueId([dialogId, LMDialogButtonKey.CANCEL]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            modal.isCancelled = true;
            modal.close(true);
          }
        }
      ])
      .title(title)
      .open();
  }

  // todo: vwiny spec-url-refactor doesn't seem to be used. - Phillip
  public openChangePasswordWindow() {
    const subscription = this.lmSpecService.getFormSpecFromRedirectActionUrl(this.changePasswordLink)
      .subscribe((response: LMActionFormUrlResponse | LMSystemException) => {

        if ((response as LMSystemException).status
          && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
            || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
          // TODO: This function is currently not in use. The caller of this function will determine what to do with busy indicator or how to display the error message.
          // this.lmBusyIndicatorService.hideBusyIndicator();
          // this.lmErrorUtilService.handleError((response as LMSystemException).statusText, (response as LMSystemException).details);
          return;
        }

        const formUrlInfo = (response as LMActionFormUrlResponse).formUrlInfo;
        const formSpec = (response as LMActionFormUrlResponse).formSpec;
        const resourceId = (response as LMActionFormUrlResponse).resourceId;
        const actionName = (response as LMActionFormUrlResponse).action;
        const actionSpec: LMActionSpec = this.lmBannerService.getChangePasswordAction(formSpec.busClassSpec.actions, 'ChangePasswordUIMenu');
        actionSpec.isChangePassword = true;
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const dataUrl = urlCompiledTemplate.expand({ resourceId, actionName });

        this.lmActionFormDialogService.getActionFormDialogDataView(dataUrl).subscribe((responseDV: any) => {
          const formDataView: LMDataView = responseDV;

          if (formDataView.status !== AppConstants.STATUS_COMPLETED) {
            this.lmLoggerService.error(responseDV);
            this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
            return;
          }

          this.lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView)
            .subscribe((actionFormDialogResult: LMActionFormDialogResult) => {
              if (actionFormDialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                // nothing to do as successful and error message are handled where action is executed
              }
            });
        });
      });

    this.subscriptionArray.push(subscription);
    return false; // todo why return false?
  }

  public openLoginWindow() {
    const loginDialog = this.lmLoginService.promptLoginDialog(this.loginPlaceholder).subscribe((result: LoginResult) => {
      if (result.status === LoginDialogStatus.RESPONSE_OK) {
        this.lmLoggerService.log(result);
      }
    });

    this.subscriptionArray.push(loginDialog);
    return false; // todo why return false?
  }

  // private onWebAppSelected($event?: any) {
  //   this.busyIndicator.open();
  //
  //   const button: any = $event.currentTarget;
  //   const url = button.getAttribute('navigation-menu-url');
  //   this.webAppTitle = button.getAttribute('title');
  //   this.webAppName = button.getAttribute('web-app-name');
  //
  //   // For webapp without a navigation menu
  //   if (!url) {
  //     this.selectedMenuSpec = [];
  //     this.webAppMenuSpecs = this.bannerMenuSpec.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
  //     let currWebAppSpec = this.bannerMenuSpec.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);
  //
  //     this.selectedMenuSpec.push(
  //         {
  //           resourceId: currWebAppSpec[ 0 ].webAppName,
  //           name: 'Home',
  //           link: currWebAppSpec[ 0 ].webAppUrl,
  //           id: 'Home',
  //           label: Soho.Locale.translate('Home'),
  //           resourceType: 'page'
  //         });
  //     this.menuSpecs[ this.webAppName ] = this.selectedMenuSpec;
  //
  //     this.markForRefresh();
  //     this.updateRequired = true;
  //
  //     this.lmContextService.updateWebApp(currWebAppSpec[ 0 ]);
  //     this.lmUrlUtilService.navigateByUrl(currWebAppSpec[ 0 ].webAppUrl);
  //
  //     this.busyIndicator.close(false);
  //   } else {
  //     this.lmSpecService.getBannerSpec(url).subscribe(
  //         response => {
  //           this.selectedMenuSpec = response.menuSpecs;
  //
  //           this.menuSpecs[ this.webAppName ] = this.selectedMenuSpec;
  //
  //           this.signOutLink = response.signOutLink;
  //
  //           this.resetMenuPersonalization();
  //
  //           this.lmBannerService.setBannerMenuSpec(this.selectedMenuSpec);
  //
  //           this.lmBannerService.updateBannerMenuSpec(response.menuSpecs, url);
  //
  //           this.markForRefresh();
  //           this.updateRequired = true;
  //
  //           this.webAppMenuSpecs = this.bannerMenuSpec.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
  //
  //           let currWebAppSpec = this.bannerMenuSpec.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);
  //           this.lmContextService.updateWebApp(currWebAppSpec[ 0 ]);
  //
  //           this.lmUrlUtilService.navigateByUrl(currWebAppSpec[ 0 ].webAppUrl);
  //
  //           this.busyIndicator.close(false);
  //         },
  //         error => {
  //           this._handleError(error);
  //         });
  //   }
  // }
  public webAppSwitcherButtonId: string;

  private onSwitchToProxyClicked(switchProxyUrl: string) {
    this.lmGlobalUtilService.navigate(switchProxyUrl, null, 'replace');
  }

  public runCreateReport(): void {
    this.lmReportPersonalizationDialogService.runReportPersonalizationDialog(
      'report',
      this.reportPlaceholder,
      this.createReportUrl,
      this.actorGroupSelectUrl,
      this.roleSelectUrl,
      this.roleGroupSelectUrl,
      this.reportFieldSearchUrl
    ).subscribe((result: LMReportPersonalizationDialogResult) => {
      if (result.status === LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_SAVED) {
        if (result.viewReport) {
          this.lmGlobalUtilService.navigateByUrl(result.reportUrl);
        } else {
          this.lmGlobalUtilService.displayToastMessage('', result.message, AppConstants.TRA_ACTION_COMPLETED);
        }
      }
      return;
    });
  }

  showBusyIndicator() {
    this.busyIndicator.open();
  }

  hideBusyIndicator() {
    this.busyIndicator.close(false);
  }

  public getBannerPersonalization() {
    const bannerPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    if (!_.isEmpty(bannerPersonalization)) {

      if (bannerPersonalization.lastOpenedMenu) {
        this.lmBannerService.lastOpenedMenuId = bannerPersonalization.lastOpenedMenu;
      } else {
        this.lmBannerService.resetLastOpenedMenu();
      }
    }
  }

  public resetMenuPersonalization() {
    const bannerPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();

    if (!_.isEmpty(bannerPersonalization) && bannerPersonalization.lastOpenedMenu) {
      bannerPersonalization.lastOpenedMenu = '';
    }
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(bannerPersonalization);

    this.lmBannerService.resetLastOpenedMenu();
  }

  public loadWebAppMenu(button: any) {
    this.showBusyIndicator();
    const loadAllLevels = this.lmBannerService.loadAllLevels();

    let url = button.getAttribute('navigationmenuurl');

    const webAppTitle = button.getAttribute('title');
    this.selectedWebAppName = button.getAttribute('id');

    if (!this.roleSwitcher) {
      this.lmBannerService.clearExpandedMenus();
    }
    // For webapp without a navigation menu
    if (!url) {
      this.selectedMenuSpec = [];
      // this.webAppMenuSpecs = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.selectedWebAppName && webAppSpec.webAppTitle);
      const currWebAppSpec = this.bannerMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.selectedWebAppName);

      this.selectedMenuSpec.push(
        {
          resourceId:   currWebAppSpec.length === 0 && this.selectedWebAppName === 'web' ? this.selectedWebAppName : currWebAppSpec[ 0 ].webAppName,
          name:         'Home',
          link:         currWebAppSpec.length === 0  && this.selectedWebAppName === 'web' ? url : currWebAppSpec[ 0 ].webAppUrl,
          id:           'Home',
          label:        Soho.Locale.translate('Home'),
          resourceType: 'page'
        });
      this.buildMenuSpecs(this.menuSpecs, this.selectedWebAppName, webAppTitle, this.selectedMenuSpec);

      this.markForRefresh();
      this.updateRequired = true;

      this.hideBusyIndicator();
    } else {
      url = loadAllLevels
        ? `${button.getAttribute('navigationmenuurl')}&allLevels=true`
        : button.getAttribute('navigationmenuurl');

      this.subscriptionArray.push(this.lmSpecService.getBannerSpec(url).subscribe(
        response => {
          if (loadAllLevels) {
            this.saveAllLevelsResponse(response);
          }

          this.selectedMenuSpec = response.menuSpecs;

          this.buildMenuSpecs(this.menuSpecs, response.webApp, response.webAppTitle, response.menuSpecs);

          this.lmBannerService.setBannerMenuSpec(this.selectedMenuSpec);

          this.lmBannerService.updateBannerMenuSpec(response.menuSpecs, url);

          this.markForRefresh();
          this.updateRequired = true;

          this.hideBusyIndicator();
        },
        error => {
          this._handleError(error);
        }));
    }
  }

  public selectMenuSpec(webAppName: string) {
    const webAppMenu = this.menuSpecs.filter(webAppSpec => webAppSpec.webAppName === webAppName);
    if (webAppMenu.length === 1) {
      return webAppMenu[ 0 ].menuSpec;
    }
    return [];
  }

  private buildMenuSpecs(
    specs: Array<LMWebAppSpec>,
    webAppName: string,
    webAppTitle: string,
    selectedMenuSpec: Array<any>
  ) {
    let pushMenu = true;

    if (specs.length > 0) {
      const menuSpec = specs.filter(webAppSpec => webAppSpec.webAppName === webAppName);
      if (menuSpec.length > 0) {
        pushMenu = false;
      }
    }
    if (pushMenu) {
      this.menuSpecs.push({
        id:          webAppName,
        webAppName,
        webAppTitle,
        menuSpec:    selectedMenuSpec
      });
    }
  }

  public ngAfterViewChecked(): void {
    if (this.updateRequired) {
      this.onWebAppMenuLoaded();
      this.updateRequired = false;
    }
  }

  public webAppId(webAppName: string): string {
    return webAppName + (this.lmContextService.testVersion() >= AppConstants.TESTVERSION_V2 ? '_webapp' : '');
  }
}
